import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faChevronDown,
  faClipboardCheck,
  faTag,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { GloveBrandAlternatives } from '../components/GloveBrandAlternatives'
import { whatsAppCtaProps } from '../lib/analytics'
import {
  getStorefrontConfig,
  getStorefrontProduct,
  listStorefrontCategories,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-bulldozer'
const PAGE_CATEGORY = 'Sarung Tangan Bulldozer'
const PRODUCT_SLUG = 'sarung-tangan-bulldozer'
const PRODUCT_IMAGE_TRANSFORM = { width: 1100, format: 'webp', quality: 78 } as const

const BULLDOZER_FALLBACK_IMAGES = [
  {
    url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/eca092f285324e848ac405f2dca35f17.jpeg',
    alt: 'Sarung Tangan Bulldozer abu-abu dan kemasan 12 pasang',
  },
  {
    url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/4f3ca9c3c13e41928202ee377cbd6074.jpeg',
    alt: 'Sarung Tangan Bulldozer abu-abu tampak depan',
  },
  {
    url: 'https://s3.us-east-2.amazonaws.com/8grams-vorme/704f03f1-2dce-4e83-827e-349562728803/2026/08/18/cdd97b20e84842e4aaf76874924b8b86.jpeg',
    alt: 'Sarung Tangan Bulldozer dan kemasan produk',
  },
] as const

function bulldozerImages(product?: StorefrontProduct | null) {
  const photos = product?.photos.length ? product.photos : BULLDOZER_FALLBACK_IMAGES
  return photos.map((photo) => ({
    src: toExternalImage(photo.url, PRODUCT_IMAGE_TRANSFORM),
    alt: 'alt_text' in photo
      ? photo.alt_text || 'Sarung Tangan Bulldozer'
      : photo.alt,
  }))
}

const DROPIFY_REASONS = [
  { icon: faBolt, title: 'Stok diperiksa melalui WhatsApp', desc: 'Sebutkan Sarung Tangan Bulldozer, jumlah, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation.' },
  { icon: faBoxesStacked, title: 'Kemasan 12 pasang per lusin', desc: 'Satu kemasan berisi 12 pasang. Jumlah aktual dikonfirmasi saat pemesanan agar perhitungan pengadaan jelas.' },
  { icon: faTag, title: 'Harga mengikuti jumlah dan tujuan', desc: 'Admin menyiapkan harga berdasarkan volume pembelian serta kota pengiriman.' },
  { icon: faTruckFast, title: 'Pengiriman ke pabrik, gudang, bengkel, atau proyek', desc: 'Pesanan dapat dikirim ke berbagai lokasi di seluruh Indonesia dengan ekspedisi yang disesuaikan.' },
]

const BULLDOZER_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Ukuran Bulldozer hanya all size. Muat untuk semua pekerja?',
    a: 'All size dengan pergelangan elastis mengikuti ukuran tangan umum. Uji dulu lewat sample beberapa pasang sebelum menyeragamkan untuk satu tim.',
  },
  {
    q: 'Kapan memilih Bulldozer dan kapan seri bintik?',
    a: 'Bulldozer memakai rajut tanpa bintik, cocok untuk barang kering yang tidak licin. Jika benda yang dipegang licin atau berminyak, admin dapat mengarahkan ke seri bintik.',
  },
  {
    q: 'Saya belum pernah lihat barangnya. Apa yang bisa dikirim admin?',
    a: 'Foto barang dari beberapa sisi, kemasan, dan labelnya dikirim lewat WhatsApp sebelum Anda mengonfirmasi pesanan.',
  },
  {
    q: 'Pengadaan kami lewat tender. Dokumen apa yang tersedia untuk Bulldozer?',
    a: 'Sampaikan standar yang diminta panitia atau tim K3. Admin memeriksa marking serta dokumen yang menyertai stok sebelum Anda memasukkannya ke penawaran.',
  },
  {
    q: 'Berapa pasang minimal untuk mencoba Bulldozer?',
    a: 'Sample bisa dipesan per pasang dengan harga satuan ditambah ongkir. Setelah cocok, lanjutkan ke pembelian per lusin isi 12 pasang.',
  },
]

export const BULLDOZER_FAQ: FaqItem[] = [
  {
    q: 'Sarung tangan Bulldozer yang tersedia seri apa?',
    a: 'Saat ini Dropify menyediakan Sarung Tangan Bulldozer rajut berwarna abu-abu. Satu kemasan berisi 12 pasang. Kirim jumlah yang dibutuhkan dan kota tujuan agar admin dapat mengecek stok serta ongkir.',
  },
  {
    q: 'Sarung tangan Bulldozer cocok untuk pekerjaan apa?',
    a: 'Model rajut tanpa bintik ini biasa dipilih untuk memindahkan karton, menata barang di gudang, bongkar muat, dan pekerjaan umum di area kering. Jika barang yang dipegang licin, berminyak, panas, atau tajam, sampaikan kondisinya kepada admin agar Anda diarahkan ke tipe sarung tangan yang lebih sesuai.',
  },
  {
    q: 'Apakah tersedia dokumen standar Bulldozer?',
    a: 'Ketersediaannya perlu diperiksa pada barang yang akan dipesan. Kirim standar wajib dari tim K3 atau dokumen tender. Admin akan mengecek marking dan dokumen produk sebelum Anda mencantumkannya dalam PO.',
  },
  {
    q: 'Berapa harga dan minimum order Bulldozer?',
    a: 'Harga dan minimum order mengikuti jumlah serta stok saat Anda memesan. Kirim kebutuhan dalam pasang atau lusin dan kota tujuan. Admin akan mengecek stok, lalu memberikan harga serta ongkir.',
  },
  {
    q: 'Apakah bisa meminta sample?',
    a: 'Bisa ditanyakan kepada admin. Gunakan sample untuk mengecek ukuran, grip, ketangkasan tangan, dan kenyamanan saat memegang benda kerja sebelum membeli dalam jumlah besar.',
  },
]

export const Route = createFileRoute('/sarung-tangan-bulldozer')({
  loader: async () => {
    const [config, categories, product, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      getStorefrontProduct(PRODUCT_SLUG).catch(() => null),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])
    return { config, categories, product, proofImages, newsPosts }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Bulldozer')
    const description =
      'Sarung tangan Bulldozer rajut abu-abu, 12 pasang per kemasan. MOQ 25 lusin, sample, kirim ke pabrik/gudang/proyek. Cek stok via WhatsApp.'
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: toExternalImage(BULLDOZER_FALLBACK_IMAGES[0].url, PRODUCT_IMAGE_TRANSFORM) },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: toExternalImage(BULLDOZER_FALLBACK_IMAGES[0].url, PRODUCT_IMAGE_TRANSFORM) },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Bulldozer', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Merek', path: '/merek' },
            { name: 'Sarung Tangan Bulldozer', path: PAGE_PATH },
          ])),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...BULLDOZER_FAQ, ...BULLDOZER_TRUST_QUESTIONS])) },
      ],
    }
  },
  component: BulldozerRoute,
})

function BulldozerRoute() {
  const { config, categories, product, proofImages, newsPosts } = Route.useLoaderData()
  return <BulldozerLandingPage config={config} categories={categories} product={product} proofImages={proofImages} newsPosts={newsPosts} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({ message, label, placement, className }: { message: string; label: string; placement: string; className: string }) {
  return (
    <a href={waLink(message)} target="_blank" rel="noopener noreferrer" {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })} className={className}>
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />{label}
    </a>
  )
}

function SectionLabel({ children, light = false }: { children: string; light?: boolean }) {
  return <p className={`text-xs font-black uppercase tracking-[0.22em] ${light ? 'text-orange-300' : 'text-orange-700'}`}>{children}</p>
}

function ProductGallery({ product, compact = false }: { product?: StorefrontProduct | null; compact?: boolean }) {
  const images = bulldozerImages(product)

  return (
    <div className={compact ? 'overflow-hidden bg-slate-100' : 'grid grid-cols-2 gap-2 bg-slate-100 p-2'}>
      {images.slice(0, compact ? 1 : 3).map((image, index) => (
        <img
          key={image.src}
          src={image.src}
          alt={image.alt}
          width={index === 0 ? 1100 : 550}
          height={index === 0 ? 820 : 550}
          loading={compact && index === 0 ? 'eager' : 'lazy'}
          fetchPriority={compact && index === 0 ? 'high' : undefined}
          decoding="async"
          className={`${index === 0 && !compact ? 'col-span-2 aspect-[4/3]' : 'aspect-square'} w-full object-contain bg-white`}
        />
      ))}
    </div>
  )
}

export function BulldozerLandingPage({
  config = null,
  categories = [],
  product = null,
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  product?: StorefrontProduct | null
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mau cek Sarung Tangan Bulldozer. Kebutuhan: ___ pasang/lusin. Pekerjaan: ___. Kota tujuan: ___. Bisa kirim stok, spesifikasi, dan harganya?'

  return (
    <div className="min-h-screen bg-[#fff8f1] text-slate-950">
      <div className="bg-orange-500 px-4 py-2 text-center text-xs font-black text-white sm:text-sm">MOQ mulai 25 lusin · 1 lusin isi 12 pasang · Sample tersedia</div>
      <header className="border-b border-white/10 bg-[#171717] text-white"><div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4"><a href="/" aria-label="Kembali ke Dropify"><LogoLockup size="sm" invert /></a><WhatsAppButton message={heroMessage} label="Periksa stok Bulldozer" placement="header" className="inline-flex items-center gap-2 rounded-full border border-orange-400/60 px-4 py-2.5 text-sm font-bold text-orange-200 transition-colors hover:bg-orange-400 hover:text-[#171717]" /></div></header>

      <main>
        <section className="relative overflow-hidden bg-[#171717] text-white">
          <div aria-hidden className="absolute -right-32 -top-32 h-[34rem] w-[34rem] rotate-12 rounded-[6rem] border-[100px] border-orange-500/5" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[1.02fr_0.98fr]">
            <div>
              <p className="inline-flex rounded-full bg-orange-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-white">Grosir sarung tangan kerja</p>
              <h1 className="mt-6 text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">Sarung tangan Bulldozer untuk <span className="text-orange-400">gudang dan pekerjaan umum.</span></h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">Rajut abu-abu tanpa bintik untuk menangani karton, menata barang, dan bongkar muat ringan di area kering. Satu kemasan berisi 12 pasang.</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row"><WhatsAppButton message={heroMessage} label="Cek stok & harga Bulldozer" placement="hero_primary" className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800" /><a href="#foto-bulldozer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">Lihat foto produk <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" /></a></div>
            </div>
            <div data-hero-product-visual className="mx-auto w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/10 bg-white p-3 shadow-2xl"><ProductGallery product={product} compact /></div>
          </div>
        </section>

        <section className="border-b border-orange-100 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">
            <div>
              <SectionLabel>Kegunaan produk</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sarung tangan rajut untuk handling barang dan pekerjaan umum</h2>
            </div>
            <div className="space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
              <p>Sarung Tangan Bulldozer biasa dipakai untuk membantu tangan tetap bersih saat memindahkan karton, menata barang di gudang, bongkar muat, dan mengerjakan pekerjaan umum di area kering.</p>
              <p>Bagian telapaknya berupa rajutan tanpa bintik atau lapisan tambahan. Untuk barang yang licin, berminyak, panas, atau tajam, sampaikan kondisi kerjanya kepada admin. Admin akan membandingkan tipe sarung tangan berdasarkan grip, risiko kerja, dan dokumen produk yang tersedia.</p>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section id="foto-bulldozer" className="scroll-mt-6 border-y border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <ProductGallery product={product} />
          </div>
        </section>

        <section className="bg-white py-16 md:py-20"><div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16"><div><SectionLabel>Layanan pemesanan</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pesan Sarung Tangan Bulldozer dalam jumlah grosir</h2><p className="mt-4 text-base leading-relaxed text-slate-600">Kirim jumlah yang dibutuhkan dan kota tujuan. Admin akan mengecek stok, menghitung harga grosir, dan menyiapkan pilihan pengiriman.</p><WhatsAppButton message={heroMessage} label="Chat admin Bulldozer" placement="why_dropify" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" /></div><div className="grid gap-4 sm:grid-cols-2">{DROPIFY_REASONS.map((item) => <article key={item.title} className="rounded-2xl border border-orange-100 bg-[#fff8f1] p-5"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#171717] text-orange-400"><FontAwesomeIcon icon={item.icon} className="h-5 w-5" /></div><h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p></article>)}</div></div></section>

        <section className="bg-white py-16 md:py-20"><div className="mx-auto max-w-4xl px-4"><div className="text-center"><SectionLabel>FAQ Bulldozer</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum meminta penawaran</h2></div><div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">{BULLDOZER_FAQ.map((item) => <details key={item.q} className="group py-5"><summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black">{item.q}<FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-orange-600 transition-transform group-open:rotate-180" /></summary><p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p></details>)}</div></div></section>

        <section className="bg-orange-500 py-14 text-white"><div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center"><div className="max-w-3xl"><p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em]"><FontAwesomeIcon icon={faClipboardCheck} className="h-4 w-4" />Pemeriksaan stok Bulldozer</p><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim jumlah dan kota tujuan Anda.</h2><p className="mt-3 text-sm leading-relaxed text-orange-50 md:text-base">Admin akan mencocokkan produk, spesifikasi, stok, harga, dan pilihan pengiriman sebelum menyiapkan quotation.</p></div><WhatsAppButton message={heroMessage} label="Minta quotation Bulldozer" placement="final_cta" className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#171717] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800" /></div></section>

        <BrandTrustSections brandName="Bulldozer" itemName="sarung tangan Bulldozer" images={proofImages} questions={BULLDOZER_TRUST_QUESTIONS} />
        <GloveBrandAlternatives currentPath={PAGE_PATH} />
      </main>
      <Footer config={config} categories={categories} />
    </div>
  )
}
